import { useEffect, useState } from "react"

import {
  type FleetOnboardingSearch,
  type FleetVertical,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import { apiClient } from "@/lib/api/client"

export interface FleetWorkspaceContextState {
  organizationId: string | null
  organizationLabel: string
  sessionError: string | null
  sessionStatus: "idle" | "loading" | "ready" | "error"
  vertical: FleetVertical
}

export function useFleetMasterDataWorkspaceContext(
  search?: FleetOnboardingSearch
): FleetWorkspaceContextState {
  const { draft } = useFleetOnboardingDraft()
  const [sessionState, setSessionState] = useState<{
    onboardingOrganizationId: string | null
    organizationId: string | null
    organizationLabel: string | null
    status: "loading" | "ready" | "error"
    error: string | null
  }>({
    onboardingOrganizationId: null,
    organizationId: null,
    organizationLabel: null,
    status: "loading",
    error: null,
  })

  useEffect(() => {
    let isActive = true

    void apiClient
      .getCurrentSession()
      .then((session) => {
        if (!isActive) {
          return
        }

        setSessionState({
          onboardingOrganizationId: session.data.onboarding?.organizationId ?? null,
          organizationId: session.data.organization?.id ?? null,
          organizationLabel: session.data.organization?.name ?? null,
          status: "ready",
          error: null,
        })
      })
      .catch((error) => {
        if (!isActive) {
          return
        }

        setSessionState({
          onboardingOrganizationId: null,
          organizationId: null,
          organizationLabel: null,
          status: "error",
          error: error instanceof Error ? error.message : "Failed to load the operator session.",
        })
      })

    return () => {
      isActive = false
    }
  }, [])

  const organizationId =
    search?.organizationId
    ?? draft.activeOrganizationId
    ?? draft.masterPersistence?.organizationId
    ?? sessionState.organizationId
    ?? sessionState.onboardingOrganizationId
    ?? null

  const organizationLabel =
    draft.organizationName.trim()
    || sessionState.organizationLabel
    || "Fleet organization"

  const vertical =
    search?.vertical
    ?? draft.selectedVertical
    ?? draft.masterPersistence?.vertical
    ?? draft.kitIssuance?.vertical
    ?? "trucking"

  return {
    organizationId,
    organizationLabel,
    sessionError: sessionState.error,
    sessionStatus: sessionState.status,
    vertical,
  }
}

export function formatFleetMasterTimestamp(value?: string | null) {
  if (!value) {
    return "Not available"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString()
}
